/**
 * Client for the backend's dev persona endpoints (LOCAL DEV / E2E ONLY).
 *
 * These routes exist only when the backend runs with DEV_AUTH_ENABLED=true
 * (development/test environments). They're deliberately excluded from the
 * generated client, which mirrors the production API surface, so the shapes
 * are validated here with zod instead.
 */

import { z } from "zod";
import { ApiError } from "@/api";

export const devPersonaSchema = z.object({
  key: z.string(),
  name: z.string(),
  email: z.string(),
  description: z.string(),
  role: z.string(),
  isActive: z.boolean(),
  devTeamRole: z.string().nullable(),
});
export type DevPersona = z.infer<typeof devPersonaSchema>;

const personasResponse = z.object({ data: z.array(devPersonaSchema) });
const tokenResponse = z.object({
  accessToken: z.string(),
  expiresIn: z.number(),
  persona: devPersonaSchema,
});
export type DevToken = z.infer<typeof tokenResponse>;

async function request(path: string, init?: RequestInit): Promise<unknown> {
  const res = await fetch(`/api/v1/dev${path}`, init);
  if (!res.ok) {
    const body = await res.json().catch(() => undefined);
    throw new ApiError(res.status, res.statusText, body);
  }
  return res.json();
}

export async function fetchDevPersonas(): Promise<DevPersona[]> {
  return personasResponse.parse(await request("/personas")).data;
}

export async function fetchDevToken(persona: string): Promise<DevToken> {
  return tokenResponse.parse(
    await request("/token", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ persona }),
    }),
  );
}
